<template>
  <v-container>
    <!-- SECTION 1: ADD NEW EMAIL CONFIGS -->
    <v-card class="mb-5">
      <v-card-title>{{ $vuetify.lang.t("$vuetify.email_config.new_email_config_title") }}</v-card-title>
      <v-card-text>
        <p>{{ $vuetify.lang.t("$vuetify.email_config.connect_new_account_txt") }}</p>
        <v-btn color="primary" :small="isExtraSmallScreen" class="mr-3 mb-3" @click="handleOAuthLogin('google')">
          <v-icon left>mdi-google</v-icon>
          {{ $vuetify.lang.t("$vuetify.email_config.connect_with_google_txt") }}
        </v-btn>
        <v-btn color="primary" :small="isExtraSmallScreen" class="mr-3 mb-3" @click="handleOAuthLogin('microsoft')">
          <v-icon left>mdi-microsoft</v-icon>
          {{ $vuetify.lang.t("$vuetify.email_config.connect_with_microsoft_txt") }}
        </v-btn>
        <v-btn color="primary" :small="isExtraSmallScreen" class="mb-3" @click="openSmtpModal">
          <v-icon left>mdi-server-network</v-icon>
          {{ $vuetify.lang.t("$vuetify.email_config.configure_smtp_txt") }}
        </v-btn>
      </v-card-text>
    </v-card>

    <!-- SECTION 2: DISPLAY EXISTING EMAIL CONFIGS -->
    <v-card>
      <v-card-title>
        {{ $vuetify.lang.t("$vuetify.email_config.existing_email_config_title") }}
      </v-card-title>

      <!-- FILTER CONTROLS -->
      <v-card-text>
        <v-row>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="filters.email"
              :label="$vuetify.lang.t('$vuetify.email_config.search_by_email_txt')"
              append-icon="mdi-magnify"
              dense
              outlined
              clearable
              hide-details
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="filters.provider"
              :items="emailProviders"
              :label="$vuetify.lang.t('$vuetify.email_config.filter_by_provider_txt')"
              dense
              outlined
              clearable
              hide-details
            ></v-select>
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="filters.status"
              :items="statusOptionsComputed"
              item-text="text"
              item-value="value"
              :label="$vuetify.lang.t('$vuetify.email_config.filter_by_status_txt')"
              dense
              outlined
              clearable
              hide-details
            ></v-select>
          </v-col>
        </v-row>
      </v-card-text>

      <v-data-table
        :headers="emailTableHeaders"
        :items="emailConfigs.items"
        :server-items-length="emailConfigs.total"
        :options.sync="options"
        :loading="isLoading"
        :footer-props="{ 'items-per-page-options': [5, 10, 20] }"
        fixed-header
        style="min-height: 50px;"
        class="elevation-1"
      >
        <template v-slot:item.provider="{ item }">
          <div class="d-flex align-center">
            <v-icon class="mr-2">
              {{ getProviderIcon(item.provider) }}
            </v-icon>
            <span class="text-capitalize">{{ item.provider }}</span>
          </div>
        </template>

        <template v-slot:item.status="{ item }">
          <v-select
            :value="tempStatus[item.id] || item.status"
            :items="statusOptionsComputed"
            item-text="text"
            item-value="value"
            @change="handleStatusChange(item, $event)"
            dense
            outlined
            hide-details
            class="status-select"
          ></v-select>
        </template>

        <template v-slot:item.created_at="{ item }">
          <span>{{ formatDateTime(item.created_at) }}</span>
        </template>

        <template v-slot:item.updated_at="{ item }">
          <span>{{ formatDateTime(item.updated_at) }}</span>
        </template>
        
        <template v-slot:item.action="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn 
                icon
                small v-bind="attrs" v-on="on" 
                @click="openConfirmationModal(item, 'delete')"
              >
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </template>
            <span>{{ $vuetify.lang.t("$vuetify.common.delete_btn") }}</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>

    <!-- SECTION 3: EMAIL RECIPIENTS -->
    <v-card class="mb-5 mt-5">
      <v-card-title>
        {{ $vuetify.lang.t("$vuetify.email_config.recipients_title") }}
        <v-spacer></v-spacer>
        <v-btn 
          color="primary" 
          :small="isExtraSmallScreen" 
          @click="openRecipientModal"
        >
          <v-icon left>mdi-account-plus</v-icon>
          {{ $vuetify.lang.t("$vuetify.email_config.add_recipient_btn") }}
        </v-btn>
      </v-card-title>
      <v-card-text class="px-4">
        <p>{{ $vuetify.lang.t("$vuetify.email_config.recipients_description") }}</p>
        
        <!-- RECIPIENT FILTER -->
        <v-row class="mb-3">
          <v-col cols="12" md="4">
            <v-text-field
              v-model="recipientFilters.search"
              :label="$vuetify.lang.t('$vuetify.email_config.search_recipients_txt')"
              append-icon="mdi-magnify"
              dense
              outlined
              clearable
              hide-details
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- RECIPIENTS DATA TABLE -->
      <v-data-table
        :headers="recipientTableHeaders"
        :items="emailRecipients.items"
        :server-items-length="emailRecipients.total"
        :loading="isLoadingRecipients"
        :options.sync="recipientOptions"
        :footer-props="{ 'items-per-page-options': [5, 10, 20] }"
        fixed-header
        style="min-height: 50px;"
        class="elevation-1"
      >
        <template v-slot:item.name="{ item }">
          <TruncateTooltip
            :text="item.name"
            :limit="100"
            :textMaxWidth="300"
            :tooltipMaxWidth="500"
          />
        </template>

        <template v-slot:item.email="{ item }">
          <span> {{ item.email || $vuetify.lang.t('$vuetify.email_config.not_available') }} </span>
        </template>

        <template v-slot:item.company_name="{ item }">
          <span>{{ item.company_name || $vuetify.lang.t('$vuetify.email_config.not_available') }}</span>
        </template>

        <template v-slot:item.department_name="{ item }">
          <span>{{ item.department_name || $vuetify.lang.t('$vuetify.email_config.not_available') }}</span>
        </template>

        <template v-slot:item.phone_number="{ item }">
          <span> {{ item.phone_number || $vuetify.lang.t('$vuetify.email_config.not_available') }} </span>
        </template>

        <template v-slot:item.created_at="{ item }">
          <span>{{ formatDateTime(item.created_at) || $vuetify.lang.t('$vuetify.email_config.not_available') }}</span>
        </template>

        <template v-slot:item.updated_at="{ item }">
          <span>{{ formatDateTime(item.updated_at) || $vuetify.lang.t('$vuetify.email_config.not_available') }}</span>
        </template>

        <template v-slot:item.action="{ item }">
          <div class="d-flex justify-end">
            <v-btn
              icon
              small
              @click="viewRecipient(item)"
              class="mr-1"
            >
              <v-icon small>mdi-eye</v-icon>
            </v-btn>
            <v-btn
              icon
              small
              @click="editRecipient(item)"
              class="mr-1"
            >
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              icon
              small
              @click="deleteRecipient(item)"
            >
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- SECTION 4: EMAIL TEMPLATES -->
    <v-card class="mt-5">
      <v-card-title>
        {{ $vuetify.lang.t("$vuetify.email_config.templates_title") }}
        <v-spacer></v-spacer>
        <v-btn 
          color="primary" 
          :small="isExtraSmallScreen" 
          @click="openTemplateModal"
        >
          <v-icon left>mdi-file-document-plus</v-icon>
          {{ $vuetify.lang.t("$vuetify.email_config.add_template_btn") }}
        </v-btn>
      </v-card-title>
      <v-card-text class="px-4">
        <p>{{ $vuetify.lang.t("$vuetify.email_config.templates_description") }}</p>
        
        <!-- TEMPLATE FILTER -->
        <v-row class="mb-3">
          <v-col cols="12" md="4">
            <v-text-field
              v-model="templateFilters.search"
              :label="$vuetify.lang.t('$vuetify.email_config.search_templates_txt')"
              append-icon="mdi-magnify"
              dense
              outlined
              clearable
              hide-details
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- TEMPLATES DATA TABLE -->
      <v-data-table
        :headers="templateTableHeaders"
        :items="emailTemplates.items"
        :server-items-length="emailTemplates.total"
        :loading="isLoadingTemplates"
        :options.sync="templateOptions"
        :footer-props="{ 'items-per-page-options': [5, 10, 20] }"
        fixed-header
        style="min-height: 50px;"
        class="elevation-1"
      >
        <template v-slot:item.name="{ item }">
          <TruncateTooltip
            :text="item.name"
            :limit="100"
            :textMaxWidth="300"
            :tooltipMaxWidth="500"
          />
        </template>

        <template v-slot:item.subject="{ item }">
          <TruncateTooltip
            :text="item.subject"
            :limit="100"
            :textMaxWidth="300"
            :tooltipMaxWidth="500"
          />
        </template>

        <template v-slot:item.body="{ item }">
          <TruncateTooltip
            :text="item.body"
            :limit="100"
            :textMaxWidth="300"
            :tooltipMaxWidth="500"
          />
        </template>

        <template v-slot:item.created_at="{ item }">
          <span>{{ formatDateTime(item.created_at) || $vuetify.lang.t('$vuetify.email_config.not_available') }}</span>
        </template>

        <template v-slot:item.updated_at="{ item }">
          <span>{{ formatDateTime(item.updated_at) || $vuetify.lang.t('$vuetify.email_config.not_available') }}</span>
        </template>

        <template v-slot:item.action="{ item }">
          <div class="d-flex justify-end">
            <v-btn
              icon
              small
              @click="viewTemplate(item)"
              class="mr-1"
            >
              <v-icon small>mdi-eye</v-icon>
            </v-btn>
            <v-btn
              icon
              small
              @click="editTemplate(item)"
              class="mr-1"
            >
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              icon
              small
              @click="deleteTemplate(item)"
            >
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- SMTP CONFIGURATION MODAL -->
    <v-dialog v-model="smtpModal" persistent max-width="600px">
      <v-card>
        <v-card-title>
          <span class="text-h5">
            {{ $vuetify.lang.t("$vuetify.email_config.smtp_config_modal_txt") }}
          </span>
        </v-card-title>
        <v-card-text>
          <v-form ref="smtpForm" @submit.prevent="saveSmtpConfig">
            <v-text-field
              outlined
              :label="$vuetify.lang.t('$vuetify.email_config.smtp_host_label')"
              v-model="smtp.host"
              :rules="[formRules.required($vuetify.lang.t('$vuetify.email_config.smtp_host_label')), formRules.host]"
              :placeholder="$vuetify.lang.t('$vuetify.email_config.smtp_host_placeholder')"
              clearable
            ></v-text-field>

            <v-text-field
              outlined
              :label="$vuetify.lang.t('$vuetify.email_config.smtp_port_label')"
              v-model.number="smtp.port"
              type="number"
              :rules="[formRules.required($vuetify.lang.t('$vuetify.email_config.smtp_port_label')), formRules.port]"
              :placeholder="$vuetify.lang.t('$vuetify.email_config.smtp_port_placeholder')"
              clearable
            ></v-text-field>

            <v-text-field
              outlined
              :label="$vuetify.lang.t('$vuetify.email_config.sender_email_label')"
              v-model="smtp.email"
              type="email"
              :rules="[formRules.required($vuetify.lang.t('$vuetify.email_config.sender_email_label')), formRules.email]"
              :placeholder="$vuetify.lang.t('$vuetify.email_config.sender_email_placeholder')"
              clearable
            ></v-text-field>

            <v-text-field
              outlined
              :label="$vuetify.lang.t('$vuetify.email_config.app_password_label')"
              v-model="smtp.password"
              :type="showPassword ? 'text' : 'password'"
              :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append="showPassword = !showPassword"
              :rules="[formRules.required($vuetify.lang.t('$vuetify.email_config.app_password_label'))]"
              :hint="$vuetify.lang.t('$vuetify.email_config.app_password_hint')"
              persistent-hint
              clearable
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            v-if="!isLoading"
            color="error"
            outlined
            @click="smtpModal = false; resetSmtpForm()"
          >
            {{ $vuetify.lang.t("$vuetify.common.cancel_btn") }}
          </v-btn>

          <v-btn
            color="primary"
            :loading="isLoading"
            @click="saveSmtpConfig"
          >
            {{ $vuetify.lang.t("$vuetify.common.save_btn") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- GENERIC CONFIRMATION MODAL -->
    <v-dialog v-model="confirmationModal" width="500" persistent>
      <v-card>
        <v-card-title class="headline primary white--text">{{ confirmationTitle }}</v-card-title>
        <v-layout class="px-5 py-5">
          <v-row class="mx-2 my-2">
            <span>{{ confirmationText }}</span>
            <slot></slot>
          </v-row>
        </v-layout>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            v-if="!isLoading"
            color="error"
            outlined
            @click="closeConfirmationModal"
          >
            {{ $vuetify.lang.t("$vuetify.common.cancel_btn") }}
          </v-btn>

          <v-btn
            color="primary"
            :loading="isLoading"
            @click="confirmAction"
          >
            {{ $vuetify.lang.t("$vuetify.common.confirm_btn") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ADD RECIPIENT MODAL -->
    <v-dialog v-model="recipientModal" persistent max-width="600px">
      <v-card>
        <v-card-title>
          <span class="text-h5">
            {{ isEditMode ? $vuetify.lang.t("$vuetify.email_config.edit_recipient_modal_title") : $vuetify.lang.t("$vuetify.email_config.add_recipient_modal_title") }}
          </span>
        </v-card-title>
        <v-card-text>
          <v-form ref="recipientForm" @submit.prevent="saveRecipient">
            <v-text-field
              outlined
              :label="$vuetify.lang.t('$vuetify.email_config.recipient_name_label')"
              v-model="recipient.name"
              :rules="[formRules.required($vuetify.lang.t('$vuetify.email_config.recipient_name_label'))]"
              :placeholder="$vuetify.lang.t('$vuetify.email_config.recipient_name_placeholder')"
              clearable
            ></v-text-field>

            <v-text-field
              outlined
              :label="$vuetify.lang.t('$vuetify.email_config.recipient_email_label')"
              v-model="recipient.email"
              type="email"
              :rules="[formRules.required($vuetify.lang.t('$vuetify.email_config.recipient_email_label')), formRules.email]"
              :placeholder="$vuetify.lang.t('$vuetify.email_config.recipient_email_placeholder')"
              clearable
            ></v-text-field>

            <v-text-field
              outlined
              :label="$vuetify.lang.t('$vuetify.email_config.recipient_company_label')"
              v-model="recipient.company_name"
              :placeholder="$vuetify.lang.t('$vuetify.email_config.recipient_company_placeholder')"
              clearable
            ></v-text-field>

            <v-text-field
              outlined
              :label="$vuetify.lang.t('$vuetify.email_config.recipient_department_label')"
              v-model="recipient.department_name"
              :placeholder="$vuetify.lang.t('$vuetify.email_config.recipient_department_placeholder')"
              clearable
            ></v-text-field>

            <v-text-field
              outlined
              :label="$vuetify.lang.t('$vuetify.email_config.recipient_phone_label')"
              v-model="recipient.phone_number"
              :rules="[formRules.phone]"
              :placeholder="$vuetify.lang.t('$vuetify.email_config.recipient_phone_placeholder')"
              clearable
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            v-if="!isLoadingRecipients"
            color="error"
            outlined
            @click="recipientModal = false; resetRecipientForm()"
          >
            {{ $vuetify.lang.t("$vuetify.common.cancel_btn") }}
          </v-btn>

          <v-btn
            color="primary"
            :loading="isLoadingRecipients"
            @click="saveRecipient"
          >
            {{ isEditMode ? $vuetify.lang.t("$vuetify.common.update_btn") : $vuetify.lang.t("$vuetify.common.save_btn") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ADD TEMPLATE MODAL -->
    <v-dialog v-model="templateModal" persistent max-width="800px">
      <v-card>
        <v-card-title>
          <span class="text-h5">
            {{ isTemplateEditMode ? $vuetify.lang.t("$vuetify.email_config.edit_template_modal_title") : $vuetify.lang.t("$vuetify.email_config.add_template_modal_title") }}
          </span>
        </v-card-title>
        <v-card-text>
          <v-form ref="templateForm" @submit.prevent="saveTemplate">
            <v-text-field
              outlined
              :label="$vuetify.lang.t('$vuetify.email_config.template_name_label')"
              v-model="template.name"
              :rules="[formRules.required($vuetify.lang.t('$vuetify.email_config.template_name_label'))]"
              :placeholder="$vuetify.lang.t('$vuetify.email_config.template_name_placeholder')"
              clearable
            ></v-text-field>

            <v-text-field
              outlined
              :label="$vuetify.lang.t('$vuetify.email_config.template_subject_label')"
              v-model="template.subject"
              :rules="[formRules.required($vuetify.lang.t('$vuetify.email_config.template_subject_label'))]"
              :placeholder="$vuetify.lang.t('$vuetify.email_config.template_subject_placeholder')"
              clearable
            ></v-text-field>

            <v-textarea
              outlined
              :label="$vuetify.lang.t('$vuetify.email_config.template_body_label')"
              v-model="template.body"
              :rules="[formRules.required($vuetify.lang.t('$vuetify.email_config.template_body_label'))]"
              :placeholder="$vuetify.lang.t('$vuetify.email_config.template_body_placeholder')"
              rows="8"
              auto-grow
            ></v-textarea>

            <v-alert
              type="info"
              outlined
              class="mt-3"
            >
              <div class="text-body-2">
                <strong>{{ $vuetify.lang.t("$vuetify.email_config.template_placeholders_title") }}:</strong>
                <ul class="mt-2">
                  <li><code>{name}</code> - {{ $vuetify.lang.t("$vuetify.email_config.placeholder_name_desc") }}</li>
                  <li><code>{email}</code> - {{ $vuetify.lang.t("$vuetify.email_config.placeholder_email_desc") }}</li>
                  <li><code>{company_name}</code> - {{ $vuetify.lang.t("$vuetify.email_config.placeholder_company_desc") }}</li>
                  <li><code>{department_name}</code> - {{ $vuetify.lang.t("$vuetify.email_config.placeholder_department_desc") }}</li>
                  <li><code>{phone_number}</code> - {{ $vuetify.lang.t("$vuetify.email_config.placeholder_phone_desc") }}</li>
                  <li><code>{secure_link}</code> - {{ $vuetify.lang.t("$vuetify.email_config.placeholder_secure_link_desc") }}</li>
                </ul>
              </div>
            </v-alert>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            v-if="!isLoadingTemplates"
            color="error"
            outlined
            @click="templateModal = false; resetTemplateForm()"
          >
            {{ $vuetify.lang.t("$vuetify.common.cancel_btn") }}
          </v-btn>

          <v-btn
            color="primary"
            :loading="isLoadingTemplates"
            @click="saveTemplate"
          >
            {{ isTemplateEditMode ? $vuetify.lang.t("$vuetify.common.update_btn") : $vuetify.lang.t("$vuetify.common.save_btn") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- VIEW RECIPIENT MODAL -->
    <v-dialog v-model="recipientViewModal" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="text-h5">
            {{ $vuetify.lang.t("$vuetify.email_config.view_recipient_modal_title") }}
          </span>
        </v-card-title>
        <v-card-text>
          <Loader v-if="isViewingRecipient" />
          <v-list v-else>
            <v-list-item>
              <v-list-item-content class="font-weight-bold">
                {{ $vuetify.lang.t("$vuetify.email_config.recipient_name_label") }}
              </v-list-item-content>
              <v-list-item-content class="align-end">
                {{ selectedRecipient?.name || $vuetify.lang.t('$vuetify.email_config.not_available') }}
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content class="font-weight-bold">
                {{ $vuetify.lang.t("$vuetify.email_config.recipient_email_label") }}
              </v-list-item-content>
              <v-list-item-content class="align-end">
                {{ selectedRecipient?.email || $vuetify.lang.t('$vuetify.email_config.not_available') }}
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content class="font-weight-bold">
                {{ $vuetify.lang.t("$vuetify.email_config.recipient_company_label") }}
              </v-list-item-content>
              <v-list-item-content class="align-end">
                {{ selectedRecipient?.company_name || $vuetify.lang.t('$vuetify.email_config.not_available') }}
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content class="font-weight-bold">
                {{ $vuetify.lang.t("$vuetify.email_config.recipient_department_label") }}
              </v-list-item-content>
              <v-list-item-content class="align-end">
                {{ selectedRecipient?.department_name || $vuetify.lang.t('$vuetify.email_config.not_available') }}
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content class="font-weight-bold">
                {{ $vuetify.lang.t("$vuetify.email_config.recipient_phone_label") }}
              </v-list-item-content>
              <v-list-item-content class="align-end">
                {{ selectedRecipient?.phone_number || $vuetify.lang.t('$vuetify.email_config.not_available') }}
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="error"
            outlined
            @click="recipientViewModal = false"
          >
            {{ $vuetify.lang.t("$vuetify.common.close_btn") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- VIEW TEMPLATE MODAL -->
    <v-dialog v-model="templateViewModal" max-width="800px">
      <v-card>
        <v-card-title>
          <span class="text-h5">
            {{ $vuetify.lang.t("$vuetify.email_config.view_template_modal_title") }}
          </span>
        </v-card-title>
        <v-card-text>
          <Loader v-if="isViewingTemplate" />
          <v-list v-else>
            <v-list-item>
              <v-list-item-content class="font-weight-bold">
                {{ $vuetify.lang.t("$vuetify.email_config.template_name_label") }}
              </v-list-item-content>
              <v-list-item-content class="align-end">
                {{ selectedTemplate?.name || $vuetify.lang.t('$vuetify.email_config.not_available') }}
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content class="font-weight-bold">
                {{ $vuetify.lang.t("$vuetify.email_config.template_subject_label") }}
              </v-list-item-content>
              <v-list-item-content class="align-end">
                {{ selectedTemplate?.subject || $vuetify.lang.t('$vuetify.email_config.not_available') }}
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content class="font-weight-bold">
                {{ $vuetify.lang.t("$vuetify.email_config.template_body_label") }}
              </v-list-item-content>
              <v-list-item-content class="w-100">
                <div class="text-pre-wrap">{{ selectedTemplate?.body || $vuetify.lang.t('$vuetify.email_config.not_available') }}</div>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="error"
            outlined
            @click="templateViewModal = false"
          >
            {{ $vuetify.lang.t("$vuetify.common.close_btn") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import getEnv from "@/config/env";
import {
  LOAD_EMAIL_CONFIGS,
  LOAD_EMAIL_PROVIDERS,
  INITIATE_OAUTH_LOGIN,
  SAVE_SMTP_CONFIG,
  DELETE_EMAIL_CONFIG,
  UPDATE_EMAIL_CONFIG_STATUS,
  LOAD_EMAIL_RECIPIENTS,
  SAVE_EMAIL_RECIPIENT,
  UPDATE_EMAIL_RECIPIENT,
  DELETE_EMAIL_RECIPIENT,
  GET_EMAIL_RECIPIENT,
  LOAD_EMAIL_TEMPLATES,
  SAVE_EMAIL_TEMPLATE,
  UPDATE_EMAIL_TEMPLATE,
  DELETE_EMAIL_TEMPLATE,
  GET_EMAIL_TEMPLATE,
  ADD_ALERT
} from "@/store/_actiontypes";
import _ from 'lodash';
import validations from "@/helpers/validations"; 
import Loader from "@/components/common/loader/Loader";
import TruncateTooltip from "@/components/common/tooltip/TruncateTooltip";
import { formatDateTime } from "@/helpers/formatter";

export default {
  name: 'EmailConfig',
  props: {
    projectId: {
      type: String,
      required: true,
    },
  },
  components: {
    Loader,
    TruncateTooltip
  },
  data() {
    return {
      snackbar: false,
      snackbarMessage: "",
      snackbarColor: "success",

      // Data table options state
      options: { page: 1, itemsPerPage: 5, sortBy: ['created_at'], sortDesc: [true] },
      
      // State for filter controls
      filters: {
        email: '',
        provider: null,
        status: null,
      },

      isLoading: true,

      headers: [
        { textKey: '$vuetify.email_config.email_address_header', value: 'email', sortable: true },
        { textKey: '$vuetify.email_config.provider_header', value: 'provider', sortable: true },
        { textKey: '$vuetify.email_config.status_header', value: 'status', sortable: true },
        { textKey: '$vuetify.email_config.created_at_header', value: 'created_at', sortable: true },
        { textKey: '$vuetify.email_config.updated_at_header', value: 'updated_at', sortable: true },
        { textKey: '$vuetify.email_config.action_header', value: 'action', sortable: false, align: 'end' },
      ],

      // Modal control
      smtpModal: false,
      recipientModal: false,
      templateModal: false,
      confirmationModal: false,
      
      // View modals
      recipientViewModal: false,
      templateViewModal: false,
      
      // Recipient modal state
      isEditMode: false,
      selectedRecipientId: null,
      selectedRecipient: null,
      
      // Template modal state
      isTemplateEditMode: false,
      selectedTemplateId: null,
      selectedTemplate: null,
      
      // State for the confirmation modal
      confirmationTitle: '',
      confirmationText: '',
      confirmationColor: 'primary',
      confirmationAction: null,
      selectedConfig: null,

      pendingStatusChange: {
        item: null,
        newStatus: null,
      },

      // SMTP form data
      smtp: {
        host: '',
        port: 587,
        email: '',
        password: '',
      },

      tempStatus: {},
      showPassword: false,
      isStatusConfirmed: false,
      
      // Recipients data
      isLoadingRecipients: false,
      recipientOptions: {
        page: 1,
        itemsPerPage: 5,
        sortBy: ['created_at'],
        sortDesc: [true],
      },
      recipientFilters: {
        search: '',
      },
      recipientHeaders: [
        { textKey: '$vuetify.email_config.name_header', value: 'name', sortable: false },
        { textKey: '$vuetify.email_config.email_address_header', value: 'email', sortable: false },
        { textKey: '$vuetify.email_config.company_header', value: 'company_name', sortable: false },
        { textKey: '$vuetify.email_config.department_header', value: 'department_name', sortable: false },
        { textKey: '$vuetify.email_config.phone_header', value: 'phone_number', sortable: false },
        { textKey: '$vuetify.email_config.created_at_header', value: 'created_at', sortable: true },
        { textKey: '$vuetify.email_config.updated_at_header', value: 'updated_at', sortable: true },
        { textKey: '$vuetify.email_config.actions_header', value: 'action', sortable: false, align: 'end' },
      ],
      
      // Recipient form data
      recipient: {
        name: '',
        email: '',
        company_name: '',
        department_name: '',
        phone_number: '',
      },
      
      // Templates data
      isLoadingTemplates: false,
      templateOptions: {
        page: 1,
        itemsPerPage: 5,
        sortBy: ['created_at'],
        sortDesc: [true],
      },
      templateFilters: {
        search: '',
      },
      templateHeaders: [
        { textKey: '$vuetify.email_config.template_name_header', value: 'name', sortable: false },
        { textKey: '$vuetify.email_config.subject_header', value: 'subject', sortable: false },
        { textKey: '$vuetify.email_config.body_header', value: 'body', sortable: false },
        { textKey: '$vuetify.email_config.created_at_header', value: 'created_at', sortable: true },
        { textKey: '$vuetify.email_config.updated_at_header', value: 'updated_at', sortable: true },
        { textKey: '$vuetify.email_config.actions_header', value: 'action', sortable: false, align: 'end' },
      ],
      
      // Template form data
      template: {
        name: '',
        subject: '',
        body: '',
      },
      isViewingRecipient: false,
      isViewingTemplate: false,

      statusOptions: [
        { textKey: '$vuetify.email_config.active_status', value: 'active' },
        { textKey: '$vuetify.email_config.inactive_status', value: 'inactive' },
      ],
    };
  },
  computed: {
    ...mapState('project', ['emailConfigs', 'emailProviders', 'emailRecipients', 'emailTemplates']),

    translateHeaders() {
      return (base) => base.map(h => {
        const mapped = { ...h };
        mapped.text = this.$vuetify.lang.t(h.textKey);
        return mapped;
      });
    },

    emailTableHeaders() {
      return this.translateHeaders(this.headers);
    },

    recipientTableHeaders() {
      return this.translateHeaders(this.recipientHeaders);
    },

    templateTableHeaders() {
      return this.translateHeaders(this.templateHeaders);
    },

    statusOptionsComputed() {
      return this.statusOptions.map(s => ({ text: this.$vuetify.lang.t(s.textKey), value: s.value }));
    },

    isExtraSmallScreen() {
      return this.$vuetify.breakpoint.width < 350;
    },

    formRules() {
      return {
        required: (fieldName) => validations.requiredTrim(fieldName),
        email: validations.email(),
        port: (v) => {
          if (!v) return true;
          const port = Number(v);
          return (
            (Number.isInteger(port) && port > 0 && port <= 65535) ||
            this.$vuetify.lang.t("$vuetify.email_config.port_range_invalid_error")
          );
        },
        host: (v) => {
          if (!v) return true;
          const smtpHostRegex = /^(?=.{1,255}$)(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[A-Za-z]{2,3}$/;
          return smtpHostRegex.test(v) || this.$vuetify.lang.t("$vuetify.email_config.host_format_invalid_error");
        },
        phone: (v) => {
          if (!v || String(v).trim() === "") return true;

          const validationResult = validations.phoneNumber(String(v));
          return validationResult === true ? true : validationResult;
        },
      };
    },
  },

  watch: {
    options: {
      handler() {
        this.fetchConfigs();
      },
      deep: true,
    },
    filters: {
      handler() {
        this.debouncedFetch();
      },
      deep: true,
    },
    
    'recipientFilters.search': {
      handler() {
        this.debouncedFetchRecipients();
      }
    },
    'templateFilters.search': {
      handler() {
        this.debouncedFetchTemplates();
      }
    },
    recipientOptions: {
      handler() {
        this.fetchRecipients();
      },
      deep: true
    },
    templateOptions: {
      handler() {
        this.fetchTemplates();
      },
      deep: true
    }
  },

  created() {
    this.loadEmailProviders();
    // debounced version of fetchConfigs that waits 500ms after the user stops typing
    this.debouncedFetch = _.debounce(() => {
        // When filters change, going back to the first page
        this.options.page = 1;
        this.fetchConfigs();
    }, 500);
    // debounced version for recipients search
    this.debouncedFetchRecipients = _.debounce(() => {
        this.fetchRecipients();
    }, 500);
    // debounced version for templates search
    this.debouncedFetchTemplates = _.debounce(() => {
        this.fetchTemplates();
    }, 500);
  },
  mounted() {
    window.addEventListener('message', this.handleOAuthMessage);
  },
  beforeDestroy() {
    window.removeEventListener('message', this.handleOAuthMessage);
  },
  methods: {
    ...mapActions('project', {
      loadEmailConfigs: LOAD_EMAIL_CONFIGS,
      loadEmailProviders: LOAD_EMAIL_PROVIDERS,
      initiateOAuth: INITIATE_OAUTH_LOGIN,
      saveSmtp: SAVE_SMTP_CONFIG,
      deleteEmailConfig: DELETE_EMAIL_CONFIG,
      updateStatus: UPDATE_EMAIL_CONFIG_STATUS,
      loadEmailRecipients: LOAD_EMAIL_RECIPIENTS,
      saveEmailRecipient: SAVE_EMAIL_RECIPIENT,
      updateEmailRecipient: UPDATE_EMAIL_RECIPIENT,
      deleteEmailRecipient: DELETE_EMAIL_RECIPIENT,
      getEmailRecipient: GET_EMAIL_RECIPIENT,
      loadEmailTemplates: LOAD_EMAIL_TEMPLATES,
      saveEmailTemplate: SAVE_EMAIL_TEMPLATE,
      updateEmailTemplate: UPDATE_EMAIL_TEMPLATE,
      deleteEmailTemplate: DELETE_EMAIL_TEMPLATE,
      getEmailTemplate: GET_EMAIL_TEMPLATE,
    }),

    ...mapActions("alert", [ADD_ALERT]),

    formatDateTime,

    async fetchRecipients() {
      this.isLoadingRecipients = true;
      try {
        const start = (this.recipientOptions.page - 1) * this.recipientOptions.itemsPerPage;
        const size = this.recipientOptions.itemsPerPage;
        const sortBy = this.recipientOptions.sortBy?.[0];
        const sortDesc = this.recipientOptions.sortDesc?.[0];
        await this.loadEmailRecipients({ 
          projectId: this.projectId,
          searchText: this.recipientFilters.search,
          start,
          size,
          sortBy,
          sortDesc
        });
      } catch (error) {
        this.showSnackBar(
          "error",
          this.$vuetify.lang.t("$vuetify.email_config.fetch_recipients_error")
        );
      } finally {
        this.isLoadingRecipients = false;
      }
    },

    openRecipientModal() {
      this.resetRecipientForm();
      this.recipientModal = true;
    },

    resetRecipientForm() {
      this.recipient = {
        name: '',
        email: '',
        company_name: '',
        department_name: '',
        phone_number: '',
      };
      this.isEditMode = false;
      this.selectedRecipientId = null;
      
      if (this.$refs.recipientForm) {
        this.$refs.recipientForm.resetValidation();
      }
    },

    async saveRecipient() {
      if (this.$refs.recipientForm.validate()) {
        this.isLoadingRecipients = true;
        try {
          const payload = {};

          Object.keys(this.recipient).forEach(key => {
            const value = this.recipient[key];
            
            if (typeof value === "string") {
              payload[key] = value.trim() || null;
            } else {
              payload[key] = value ?? null;
            }
          });
          
          let response;
          if (this.isEditMode) {
            response = await this.updateEmailRecipient({ 
              projectId: this.projectId,
              recipientId: this.selectedRecipientId, 
              payload 
            });
          } else {
            response = await this.saveEmailRecipient({ 
              projectId: this.projectId, 
              payload 
            });
          }
          
          this.resetRecipientForm();
          this.recipientModal = false;
          this.showSnackBar("success", response?.data?.detail?.response);
          // Refresh the list to show the updated/new item
          await this.fetchRecipients();
        } catch (error) {
          const errorMessage = error.response?.data?.detail?.error || 
                             this.$vuetify.lang.t(this.isEditMode ? "$vuetify.email_config.update_recipient_error" : "$vuetify.email_config.save_recipient_error");
          this.showSnackBar("error", errorMessage);
        } finally {
          this.isLoadingRecipients = false;
        }
      }
    },

    async viewRecipient(recipient) {
      this.recipientViewModal = true;
      this.isViewingRecipient = true;

      try {
        const response = await this.getEmailRecipient({ 
          projectId: this.projectId,
          recipientId: recipient.recipient_id 
        });
        const recipientData = response?.data?.detail?.response;
        
        // Set selected recipient for view modal
        this.selectedRecipient = recipientData;
      } catch (error) {
        this.showSnackBar("error", this.$vuetify.lang.t("$vuetify.email_config.fetch_recipient_error"));
      } finally {
        this.isViewingRecipient = false;
      }
    },

    editRecipient(recipient) {
      // Populate form with recipient data
      this.recipient = {
        name: recipient.name || '',
        email: recipient.email || '',
        company_name: recipient.company_name || '',
        department_name: recipient.department_name || '',
        phone_number: recipient.phone_number || '',
      };
      
      this.selectedRecipientId = recipient.recipient_id;
      this.isEditMode = true;
      this.recipientModal = true;
    },

    deleteRecipient(recipient) {
      this.confirmationTitle = this.$vuetify.lang.t("$vuetify.email_config.delete_recipient_title");
      this.confirmationText = this.$vuetify.lang.t("$vuetify.email_config.delete_recipient_confirmation").replace("{name}", `"${recipient.name}"`);
      this.confirmationColor = 'error';
      this.confirmationAction = 'delete_recipient';
      this.selectedConfig = recipient;
      this.confirmationModal = true;
    },

    async fetchTemplates() {
      this.isLoadingTemplates = true;
      try {
        const start = (this.templateOptions.page - 1) * this.templateOptions.itemsPerPage;
        const size = this.templateOptions.itemsPerPage;
        const sortBy = this.templateOptions.sortBy?.[0];
        const sortDesc = this.templateOptions.sortDesc?.[0];
        await this.loadEmailTemplates({ 
          projectId: this.projectId,
          searchText: this.templateFilters.search,
          start,
          size,
          sortBy,
          sortDesc
        });
      } catch (error) {
        this.showSnackBar(
          "error",
          this.$vuetify.lang.t("$vuetify.email_config.fetch_templates_error")
        );
      } finally {
        this.isLoadingTemplates = false;
      }
    },

    openTemplateModal() {
      this.resetTemplateForm();
      this.templateModal = true;
    },

    resetTemplateForm() {
      this.template = {
        name: '',
        subject: '',
        body: '',
      };
      this.isTemplateEditMode = false;
      this.selectedTemplateId = null;
      
      if (this.$refs.templateForm) {
        this.$refs.templateForm.resetValidation();
      }
    },

    async viewTemplate(template) {
      this.templateViewModal = true;
      this.isViewingTemplate = true;

      try {
        const response = await this.getEmailTemplate({ 
          projectId: this.projectId,
          templateId: template.template_id 
        });
        const templateData = response?.data?.detail?.response;
        
        // Set selected template for view modal
        this.selectedTemplate = templateData;
      } catch (error) {
        this.showSnackBar("error", this.$vuetify.lang.t("$vuetify.email_config.fetch_template_error"));
      } finally {
        this.isViewingTemplate = false;
      }
    },

    editTemplate(template) {
      // Populate form with template data
      this.template = {
        name: template.name || '',
        subject: template.subject || '',
        body: template.body || '',
      };
      
      this.selectedTemplateId = template.template_id;
      this.isTemplateEditMode = true;
      this.templateModal = true;
    },

    deleteTemplate(template) {
      this.confirmationTitle = this.$vuetify.lang.t("$vuetify.email_config.delete_template_title");
      this.confirmationText = this.$vuetify.lang.t("$vuetify.email_config.delete_template_confirmation").replace("{name}", `"${template.name}"`);
      this.confirmationColor = 'error';
      this.confirmationAction = 'delete_template';
      this.selectedConfig = template;
      this.confirmationModal = true;
    },

    async saveTemplate() {
      if (this.$refs.templateForm.validate()) {
        this.isLoadingTemplates = true;
        try {
          const payload = {};
          Object.keys(this.template).forEach(key => {
            if (this.template[key] && this.template[key].trim()) {
              payload[key] = this.template[key].trim();
            }
          });
          
          let response;
          if (this.isTemplateEditMode) {
            response = await this.updateEmailTemplate({ 
              projectId: this.projectId,
              templateId: this.selectedTemplateId, 
              payload 
            });
          } else {
            response = await this.saveEmailTemplate({ 
              projectId: this.projectId, 
              payload 
            });
          }
          
          this.resetTemplateForm();
          this.templateModal = false;
          this.showSnackBar("success", response?.data?.detail?.response);
          // Refresh the list to show the updated/new item
          await this.fetchTemplates();
        } catch (error) {
          const errorMessage = error.response?.data?.detail?.error || 
                             this.$vuetify.lang.t(this.isTemplateEditMode ? "$vuetify.email_config.update_template_error" : "$vuetify.email_config.save_template_error");
          this.showSnackBar("error", errorMessage);
        } finally {
          this.isLoadingTemplates = false;
        }
      }
    },


    showSnackBar(type = "success", message = "") {
      const color = type === "success" ? "success" : "error";
      this.$store.dispatch(
        `alert/${ADD_ALERT}`,
        {
          message,
          color,
        },
        { root: true }
      );
    },

    fetchConfigs() {
      this.isLoading = true;
      const { page, itemsPerPage, sortBy, sortDesc } = this.options;
      this.loadEmailConfigs({
        projectId: this.projectId,
        page: page,
        perPage: itemsPerPage,
        sortBy: sortBy,
        sortDesc: sortDesc,
        filters: this.filters,
      }).finally(() => {
        this.isLoading = false;
      });
    },
    
    getProviderIcon(provider) {
        const icons = { google: 'mdi-google', microsoft: 'mdi-microsoft', smtp: 'mdi-server-network' };
        return icons[provider] || 'mdi-email';
    },

    toggleStatus(item) {
      const action = item.status === 'active' ? 'deactivate' : 'activate';
      this.openConfirmationModal(item, action);
    },

    async handleOAuthLogin(provider) {
      try {
        const popup = window.open('', '_blank', 'width=600,height=700');
        const data = await this.initiateOAuth({ projectId: this.projectId, provider });
        if (data && data.authorization_url) {
          popup.location.href = data.authorization_url;
        } else {
          popup.close();
        }
      } catch (error) {
        this.showSnackBar(
          "error",
          this.$vuetify.lang.t("$vuetify.email_config.oauth_initiate_error")
        );
      }
    },

    handleOAuthMessage(event) {
      const expectedOrigin = new URL(getEnv("VUE_APP_BACKEND_API")).origin;

      // For security purpose: checking if message came from expected origin or not
      if (event.origin !== expectedOrigin) return;

      const { status, payload } = event.data ?? {};

      if (typeof status !== "string" || !status.startsWith("oauth-")) return;

      const providersMap = { google: 'Google', microsoft: 'Microsoft' };
      const providerName = providersMap[payload?.provider] || payload?.provider;


      if (status === 'oauth-success') {
        this.showSnackBar(
          "success",
          `${providerName} ${this.$vuetify.lang.t("$vuetify.email_config.oauth_connected_success")}`
        );
        this.fetchConfigs();
      } else if (status === 'oauth-error') {
        this.showSnackBar(
          "error",
          `${providerName} ${this.$vuetify.lang.t("$vuetify.email_config.oauth_connected_failed")}`
        );
      }
    },
    
    openSmtpModal() {
      this.resetSmtpForm();
      this.smtpModal = true
    },

    resetSmtpForm() {
      this.smtp = {
        host: '',
        port: 587,
        email: '',
        password: '',
      };
      this.showPassword = false;

      if (this.$refs.smtpForm) {
        this.$refs.smtpForm.resetValidation();
      }
    },

    async saveSmtpConfig() {
      if (this.$refs.smtpForm.validate()) {
        this.isLoading = true;
        try {
            let res = await this.saveSmtp({ projectId: this.projectId, payload: this.smtp });
            this.resetSmtpForm();
            this.smtpModal = false;
            this.showSnackBar("success", res?.data?.message);
            this.fetchConfigs();
        } catch (error) {
          this.showSnackBar(
            "error",
            this.$vuetify.lang.t("$vuetify.email_config.smtp_save_error")
          );
        } finally {
          this.isLoading = false;
        }
      }
    },

    handleStatusChange(item, newStatus) {
      // This function is called by the v-select's @change event.
      // It stores the context and opens the confirmation modal.
      if (item.status === newStatus) return; // Do nothing if the value is the same
      
      this.pendingStatusChange.item = item;
      this.pendingStatusChange.newStatus = newStatus;

      this.tempStatus[item.id] = newStatus;

      this.openConfirmationModal(item, 'change_status');
    },

    openConfirmationModal(config, action) {
      this.selectedConfig = config;
      this.confirmationAction = action;
      this.isStatusConfirmed = false;

      switch (action) {
        case "delete": {
          this.confirmationTitle = this.$vuetify.lang.t(
            "$vuetify.email_config.confirm_delete_title"
          );

          const textTemplate = this.$vuetify.lang.t(
            "$vuetify.email_config.confirm_delete_text"
          );

          this.confirmationText = textTemplate.replace(
            "{email}",
            config.email
          );
          break;
        }

        case "change_status": {
          const { newStatus } = this.pendingStatusChange;

          this.confirmationTitle = this.$vuetify.lang.t(
            "$vuetify.email_config.confirm_status_title"
          );

          const textTemplate = this.$vuetify.lang.t(
            "$vuetify.email_config.confirm_status_text"
          );

          this.confirmationText = textTemplate
            .replace("{email}", config.email)
            .replace("{status}", newStatus);
          break;
        }
      }

      this.confirmationModal = true;
    },

    closeConfirmationModal() {
      if (
        this.confirmationAction === 'change_status' &&
        this.pendingStatusChange.item &&
        !this.isStatusConfirmed
      ) {
        const item = this.pendingStatusChange.item;
        this.$set(this.tempStatus, item.id, item.status);
      }
      
      this.confirmationModal = false;
      this.selectedConfig = null;
      this.confirmationAction = null;
      this.pendingStatusChange = { item: null, newStatus: null };
    },

    async confirmAction() {
      if (!this.confirmationAction) return;

      const { projectId } = this;
      const currentPageOptions = { page: this.options.page, perPage: this.options.itemsPerPage };

      try {
        this.isLoading = true;
        if (this.confirmationAction === 'delete') {
          const configId = this.selectedConfig.id;
          await this.deleteEmailConfig({ projectId, configId, currentPageOptions });
          this.showSnackBar("success", this.$vuetify.lang.t("$vuetify.email_config.delete_success_msg"));
          this.fetchConfigs();
        } 
        else if (this.confirmationAction === 'delete_recipient') {
          const recipientId = this.selectedConfig.recipient_id;
          await this.deleteEmailRecipient({ 
            projectId: this.projectId,
            recipientId 
          });
          this.showSnackBar("success", this.$vuetify.lang.t("$vuetify.email_config.delete_recipient_success"));
          this.fetchRecipients();
        }
        else if (this.confirmationAction === 'delete_template') {
          const templateId = this.selectedConfig.template_id;
          await this.deleteEmailTemplate({ 
            projectId: this.projectId,
            templateId 
          });
          this.showSnackBar("success", this.$vuetify.lang.t("$vuetify.email_config.delete_template_success"));
          this.fetchTemplates();
        }
        else if (this.confirmationAction === 'change_status') {
          const { item, newStatus } = this.pendingStatusChange;
          const configId = item.id;

          await this.updateStatus({ projectId, configId, newStatus, currentPageOptions });

          this.$set(this.tempStatus, item.id, newStatus);
          this.isStatusConfirmed = true;

          this.showSnackBar(
            "success",
            this.$vuetify.lang.t("$vuetify.email_config.status_change_success_msg")
          );

          this.fetchConfigs();
        }
      } catch (error) {
        this.showSnackBar(
          "error",
          `${this.$vuetify.lang.t("$vuetify.email_config.action_failed")}: ${this.confirmationAction}`
        );
      } finally {
        this.closeConfirmationModal();
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.mb-5 {
  margin-bottom: 2.5rem;
}

.email-list-container {
  max-height: 400px;
  overflow-y: auto;
}

.v-list-item__action {
  gap: 8px;
}

.status-select {
  max-width: 150px;
  text-transform: capitalize;
}
</style>